import React from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuthPageUrl } from '../support/AuthPageUrl';
import {
  getInitialParams,
  updateInitialParams,
} from '../support/InitialParams';
import { fetchJson } from '../support/useFetch';

export const AUTH_TOKEN_KEY = 'app:auth-token';

type ValidationResult =
  | {
      success: true;
      token: string;
    }
  | { success: false };

async function validateToken() {
  const { auth: token } = getInitialParams();
  if (
    location.pathname !== '/login' ||
    typeof token !== 'string' ||
    !token.length
  ) {
    return null;
  }
  return await fetchJson<ValidationResult>('/api/auth/validate', {
    body: { token },
  });
}

type Props = {
  children: React.ReactNode;
};

export default function AuthTokenValidator(props: Props) {
  const getAuthPageUrl = useAuthPageUrl();

  const { error, isLoading } = useQuery(
    ['auth-token-validate'],
    () => validateToken(),
    {
      onError: (error) => {
        // eslint-disable-next-line no-console
        console.error(error);
      },
      onSuccess: (data) => {
        if (!data) {
          // No token to validate
          return;
        }
        const { next } = getInitialParams();
        const nextPath =
          typeof next === 'string' && next.startsWith('/') ? next : '/';
        updateInitialParams((params) => {
          const { auth, next, ...otherParams } = params;
          return otherParams;
        });
        if (data.success) {
          const token = String(data.token);
          sessionStorage.setItem(AUTH_TOKEN_KEY, token);
          // Note: We're not using React Router here since this component is not
          // within the router's provider
          history.replaceState({}, document.title, nextPath);
        } else {
          location.replace(getAuthPageUrl(nextPath));
        }
      },
    },
  );

  if (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    // TODO: Display a button to go back to the login page?
    return <p>{t('Error: {errorMessage}', { errorMessage })}</p>;
  }
  if (isLoading) {
    return null;
  }
  return <>{props.children}</>;
}
